import * as RxSlider from '@radix-ui/react-slider';
import { type VariantProps, tv } from 'tailwind-variants';

const track = tv({
  base: 'relative h-[9px] w-full grow rounded-full',
  variants: {
    trackColor: {
      duration: 'bg-gray-15 dark:bg-gray-90',
      editor: 'bg-gray-30 dark:bg-gray-60',
      player: 'dark:bg-gray-75 bg-gray-30',
      fromVideo: 'bg-violet/20',
      transparent: 'bg-transparent',
    },
    size: {
      sm: 'h-[3px]',
      lg: 'h-[9px]',
    },
    disabled: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      trackColor: 'player',
      disabled: true,
      className: 'bg-gray-45 dark:bg-gray-45',
    },
  ],
  defaultVariants: {
    trackColor: 'editor',
    size: 'lg',
  },
});

const range = tv({
  base: 'absolute h-full rounded-full',
  variants: {
    rangeColor: {
      player: 'bg-gray-90 dark:bg-gray-30',
      editor: 'bg-gray-90 dark:bg-white',
      blue: 'bg-blue',
      fromVideo: 'bg-violet',
      transparent: 'bg-transparent',
    },
    disabled: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      rangeColor: ['player', 'blue'],
      disabled: true,
      className: 'bg-gray-45 dark:bg-gray-45',
    },
  ],
  defaultVariants: {
    rangeColor: 'editor',
    disabled: false,
  },
});

interface SliderProps {
  'aria-label'?: string;
  'aria-valuetext'?: string;
  currentValue: number;
  onChange: (value: number) => void;
  max: number;
  min?: number;
  trackColor?: VariantProps<typeof track>['trackColor'];
  rangeColor?: VariantProps<typeof range>['rangeColor'];
  size?: VariantProps<typeof track>['size'];
  id?: string;
  className?: string;
  step?: number;
  disabled?: boolean;
  onMouseUp?: () => void;
  showThumbWhenDisabled?: boolean;
  thumbClassName?: string;
  trackClassName?: string;
}

// TODO: Rework the variants. Not all of them are in use anymore and it's confusing.
export default function Slider({
  'aria-label': ariaLabel,
  'aria-valuetext': ariaValueText,
  currentValue,
  onChange,
  max,
  min = 1,
  trackColor,
  rangeColor,
  id,
  className = '',
  step = 1,
  disabled,
  onMouseUp,
  size = 'lg',
  showThumbWhenDisabled = false,
  thumbClassName = '',
  trackClassName = '',
}: SliderProps) {
  return (
    <RxSlider.Root
      className={`relative flex h-5 w-full touch-none items-center ${className}`}
      value={[currentValue]}
      onValueChange={([value]) => onChange(value)}
      onMouseUp={onMouseUp}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
    >
      <RxSlider.Track
        className={`${track({ trackColor, disabled, size })} ${trackClassName}`}
      >
        <RxSlider.Range className={range({ rangeColor, disabled })} />
      </RxSlider.Track>
      {(!disabled || showThumbWhenDisabled) && (
        <RxSlider.Thumb
          id={id}
          aria-label={ariaLabel}
          aria-valuetext={ariaValueText}
          className={`border-blue  dark:bg-gray-90 block h-5 w-5 rounded-full border-[3px] bg-white transition-colors hover:dark:border-white ${thumbClassName}`}
        />
      )}
    </RxSlider.Root>
  );
}
